import { useCallback, useEffect, useMemo, useState } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import {
  alpha,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  makeStyles,
  Popover,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Close, Close as CloseIcon, ExpandMore } from '@material-ui/icons';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import { format, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { useSnackbar } from 'notistack';

import { getDateRangeOptions } from 'utils/getDateRangeOptions';

const DATE_FORMAT = 'dd/MM/yyyy';
const INVALID_DATE_MESSAGE = 'Formato de data inválida';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  input: {
    fontSize: typography.pxToRem(14),
    paddingLeft: 0,
    paddingRight: spacing(1),
  },
  inputLabel: {
    fontSize: typography.pxToRem(14),
    color: alpha(palette.text.primary, 0.7),
  },
  popover: {
    marginTop: spacing(1),
  },
  divider: {
    backgroundColor: palette.text.secondary,
    width: 10,
    height: 2,
    marginInline: spacing(1),
  },
  buttonWrapper: {
    position: 'relative',
  },
  floatingLabel: {
    position: 'absolute',
    top: -6,
    left: 8,
    backgroundColor: palette.background.paper,
    paddingInline: 4,
    fontSize: typography.pxToRem(11),
    color: alpha(palette.text.primary, 0.5),
    zIndex: 1,
    lineHeight: 1,
  },
  buttonTrigger: {
    border: `1px solid ${alpha(palette.action.disabled, 0.25)}`,
    color: alpha(palette.text.primary, palette.type === 'dark' ? 0.6 : 0.45),
    textTransform: 'none',
    paddingInline: spacing(1.5),
    transition: 'none',
    width: '100%',
    justifyContent: 'space-between',

    '&:hover': {
      border: `1px solid ${palette.text.primary}`,
      backgroundColor: 'transparent',
    },

    '&:focus': {
      border: `1px solid ${palette.text.primary}`,
      backgroundColor: 'transparent',
    },
  },
  buttonTriggerActive: {
    color: palette.text.primary,
  },
  adornedStart: {
    '& .MuiIconButton-root': {
      padding: 4,
      marginLeft: spacing(1),
    },
  },
  adornedEnd: {
    width: 20,
    height: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: '50%',

    '&:hover': {
      backgroundColor: alpha(palette.action.disabled, 0.1),
    },
  },
}));

function formatDateRange(
  start: MaterialUiPickersDate,
  end: MaterialUiPickersDate,
  label = 'Data',
) {
  if (start && isValid(start) && !end) {
    return format(start, DATE_FORMAT);
  }

  if (!start && end && isValid(end)) {
    return format(end, DATE_FORMAT);
  }

  if (start && end && isValid(start) && isValid(end)) {
    return `${format(start, DATE_FORMAT)} - ${format(end, DATE_FORMAT)}`;
  }

  return label;
}

interface RangeDateFilterProps {
  rangeDate: MaterialUiPickersDate[];
  onFilterRangeDate: (dates: MaterialUiPickersDate[]) => void;
  onResetRangeDate: () => void;
  label?: string;
}

export default function RangeDateFilter({
  rangeDate,
  onFilterRangeDate,
  onResetRangeDate,
  label = 'Data',
}: RangeDateFilterProps) {
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [chipSelected, setChipSelected] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<MaterialUiPickersDate>(null);
  const [endDate, setEndDate] = useState<MaterialUiPickersDate>(null);

  useEffect(() => {
    if (rangeDate.length > 0) {
      setStartDate(rangeDate[0]);
      setEndDate(rangeDate[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
      setChipSelected(null);
    }
  }, [rangeDate]);

  const resetRangeDate = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setChipSelected(null);
    onResetRangeDate();
  }, [onResetRangeDate]);

  const handleStartDateChange = useCallback(
    date => {
      setChipSelected(null);
      setStartDate(date);

      if (!date || !isValid(date)) return;

      if (!endDate || !isValid(endDate)) {
        onFilterRangeDate([date]);
        return;
      }

      if (date <= endDate) {
        onFilterRangeDate([date, endDate]);
        return;
      }

      enqueueSnackbar('A data inicial precisa ser menor que a data final!', {
        variant: 'warning',
      });
      setStartDate(null);
    },
    [endDate, enqueueSnackbar, onFilterRangeDate],
  );

  const handleEndDateChange = useCallback(
    date => {
      setEndDate(date);

      if (!date || !isValid(date)) return;

      if (!startDate || !isValid(startDate)) {
        onFilterRangeDate([null, date]);
        return;
      }

      if (date >= startDate) {
        onFilterRangeDate([startDate, date]);
        return;
      }

      enqueueSnackbar('A Data final precisa ser maior que a data inicial!', {
        variant: 'warning',
      });
      setEndDate(null);
    },
    [startDate, onFilterRangeDate, enqueueSnackbar],
  );

  const handleQuickSelect = useCallback(
    (index, option) => {
      setChipSelected(index);
      setStartDate(option.startDate);
      setEndDate(option.endDate);
      onFilterRangeDate([option.startDate, option.endDate]);
    },
    [onFilterRangeDate],
  );

  const displayText = useMemo(
    () => formatDateRange(rangeDate[0] ?? null, rangeDate[1] ?? null, label),
    [rangeDate, label],
  );

  const hasDateRange =
    (startDate && isValid(startDate)) || (endDate && isValid(endDate));

  return (
    <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
      <PopupState variant="popover" popupId="date-range-popover">
        {popupState => (
          <>
            <Box className={classes.buttonWrapper}>
              {hasDateRange && (
                <Typography className={classes.floatingLabel}>
                  {label}
                </Typography>
              )}
              <Button
                id="popover"
                color="inherit"
                disableTouchRipple
                disableRipple
                disableFocusRipple
                endIcon={
                  hasDateRange ? (
                    <Box className={classes.adornedEnd}>
                      <Close
                        style={{ fontSize: 16 }}
                        onClick={e => {
                          e.stopPropagation();
                          resetRangeDate();
                          onFilterRangeDate([]);
                        }}
                      />
                    </Box>
                  ) : (
                    <ExpandMore style={{ fontSize: 18 }} />
                  )
                }
                variant="outlined"
                size="large"
                className={clsx(classes.buttonTrigger, {
                  [classes.buttonTriggerActive]: hasDateRange,
                })}
                {...bindTrigger(popupState)}
              >
                <Typography variant="body2" noWrap>
                  {displayText}
                </Typography>
              </Button>
            </Box>

            <Popover
              {...bindPopover(popupState)}
              onClose={() => {
                if (
                  startDate &&
                  isValid(startDate) &&
                  (!endDate || !isValid(endDate))
                ) {
                  setEndDate(startDate);
                  onFilterRangeDate([startDate, startDate]);
                }
                popupState.close();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              className={classes.popover}
            >
              <Box width={450} p={2}>
                <Box mb={2}>
                  {getDateRangeOptions().map((option, index) => (
                    <Chip
                      key={`chip-${option.description}-${index + 1}`}
                      label={option.description}
                      onClick={() => handleQuickSelect(index, option)}
                      style={{
                        marginRight: 4,
                        marginBottom: 4,
                        fontWeight: 600,
                        backgroundColor: alpha(
                          theme.palette.action.disabledBackground,
                          0.05,
                        ),
                        ...(chipSelected === index && {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        }),
                      }}
                    />
                  ))}
                </Box>

                <Typography
                  variant="body2"
                  style={{ fontWeight: 500, marginTop: 8 }}
                >
                  Personalizado
                </Typography>

                <Box display="flex" mt={1.5} style={{ alignItems: 'center' }}>
                  <KeyboardDatePicker
                    autoOk
                    variant="dialog"
                    inputVariant="outlined"
                    label="Data inicial"
                    cancelLabel="Cancelar"
                    format={DATE_FORMAT}
                    invalidDateMessage={INVALID_DATE_MESSAGE}
                    value={startDate}
                    onChange={handleStartDateChange}
                    InputAdornmentProps={{ position: 'start' }}
                    InputProps={{
                      classes: { adornedStart: classes.adornedStart },
                      endAdornment: !!startDate && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setStartDate(null);
                              setChipSelected(null);
                              onFilterRangeDate(endDate ? [null, endDate] : []);
                            }}
                            size="small"
                          >
                            <CloseIcon style={{ width: 20, height: 20 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      className: classes.input,
                    }}
                    InputLabelProps={{
                      className: classes.inputLabel,
                    }}
                  />

                  <Divider className={classes.divider} />

                  <KeyboardDatePicker
                    autoOk
                    variant="dialog"
                    inputVariant="outlined"
                    label="Data final"
                    cancelLabel="Cancelar"
                    format={DATE_FORMAT}
                    invalidDateMessage={INVALID_DATE_MESSAGE}
                    value={endDate || null}
                    onChange={handleEndDateChange}
                    InputAdornmentProps={{ position: 'start' }}
                    InputProps={{
                      classes: { adornedStart: classes.adornedStart },
                      endAdornment: !!endDate && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setEndDate(null);
                              setChipSelected(null);
                              onFilterRangeDate(
                                startDate ? [startDate, null] : [],
                              );
                            }}
                            size="small"
                          >
                            <CloseIcon style={{ width: 20, height: 20 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      className: classes.input,
                    }}
                    InputLabelProps={{
                      className: classes.inputLabel,
                    }}
                  />
                </Box>
              </Box>
            </Popover>
          </>
        )}
      </PopupState>
    </MuiPickersUtilsProvider>
  );
}
